import { type FC, useState } from "react";
import { useStateWithDeps } from "use-state-with-deps";
import { styled } from "@linaria/react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { t } from "@/themes/theme.ts";
import { Row } from "@/components/common/row.ts";
import { ButtonIcon } from "@/components/buttons/ButtonIcon.tsx";
import { BasicInput } from "@/components/inputs/shared.ts";
import { SharedChatStyle } from "@/components/chat/ChatInput.tsx";
import { IconFlag } from "@/icons/IconFlag.tsx";
import { IconTick } from "@/icons/IconTick.tsx";
import { IconPlus } from "@/icons/IconPlus.tsx";
import { IconX } from "@/icons/IconX.tsx";
import { ModalSimple } from "@/components/modal/ModalSimple.tsx";
import { type HeldItem, type HeldReplyAtoms, postChatResponseApprove } from "@/api/chat.ts";
import { useClaimSession } from "@/helpers/useClaimSession.ts";
import { usePendingToast } from "@/helpers/usePendingToast.ts";
import { MessageBody } from "@/components/chat/Message.ts";
import type { HeldReplyState } from "@/stores/useChatStore.ts";
import { toastAndWarn } from "@/helpers/toast.ts";

interface ChatReviewPanelProps {
  flowGeneratorSessionId: number;
  heldState: Extract<HeldReplyState, { $type: "held" }>;
  // Reports a successful approve (with the resolved hold's identity) so the
  // parent can dismiss this panel directly — dismissal must never depend on
  // the server reflecting the approve back through the probe
  onResolved: (identity: string) => void;
  // Fires when the reviewer claims the session outright from this panel — the
  // parent stops treating the ownership as a review assignment (unlocks input)
  onClaimed?: () => void;
}

const PanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 0.75rem;
  padding: 0.75rem ${t.chatPaddingSides};
  border-top: ${t.brandBorderSegment};
  background-color: color-mix(in srgb, ${t.brandPastelBlue} 10%, white);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HeaderTitle = styled.h3`
  font-size: var(--14px);
  font-weight: 600;
`;

const SectionLabel = styled.h4`
  font-size: var(--13px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${t.brandTextPrimary};

  span {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 400;
    margin-left: 0.5rem;
  }
`;

const DimensionList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-left: 1.25rem;
  font-size: var(--13px);
`;

const FeedbackInput = styled(BasicInput)`
  width: 100%;
`;

const OverrideText = styled.p`
  padding: 1rem 0;
`;

// e.g. "age_inappropriate_content" -> "age inappropriate content"
const formatDimension = (dimension: string) => dimension.replaceAll("_", " ");

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/**
 * Why an item was flagged: the atoms verdict plus every flagged
 * dimension/reason, straight from the judgement metadata.
 */
const AtomsSummary: FC<{ atoms?: HeldReplyAtoms }> = ({ atoms }) => {
  if (!atoms || atoms.flaggedDimensions.length === 0) return null;

  return (
    <DimensionList>
      {atoms.flaggedDimensions.map((flagged) => (
        <li key={flagged.dimension}>
          <b>{formatDimension(flagged.dimension)}</b> — {flagged.reason}
        </li>
      ))}
    </DimensionList>
  );
};

const holdMeta = (item?: HeldItem): string | null => {
  if (!item) return null;

  const parts = [
    item.atoms?.verdict,
    item.atoms?.errorCode,
    item.dateCreated
      ? `sent ${formatTime(item.dateCreated)}`
      : item.atoms
        ? formatTime(item.atoms.judgedAt)
        : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(" · ") : null;
};

/**
 * Pre-claim review surface for a safety hold — a withheld AI draft, the
 * student's own flagged message (the AI paused itself before answering), or
 * both at once, each clearly labelled. The reviewer sees what was flagged and
 * why (atoms verdict + dimensions), then either approves the hold as a false
 * positive or claims the session instead. Held text lives only here — never in
 * chat history and never anywhere a student could see.
 *
 * Editing is offered ONLY for a lone held AI reply: a held student message can
 * never be edited (the server 400s an editedMessage while one is present), so
 * when one exists any held draft renders read-only and approve sends no edit.
 *
 * After a successful approve the cached response is stamped/cleared, which
 * resolves the hold and brings this panel down. The AI auto-send switch
 * deliberately lives on the ChatAiAutoSendBar below this panel, not here.
 */
export const ChatReviewPanel: FC<ChatReviewPanelProps> = ({
  flowGeneratorSessionId,
  heldState,
  onResolved,
  onClaimed,
}) => {
  const { reply, student } = heldState;

  // A held student message forbids edits entirely — the draft (if any) 4is
  // display-only and approve must never send editedMessage
  const canEdit = !!reply && !student;

  const [text, setText] = useStateWithDeps(reply?.message ?? "", [heldState.identity]);
  const [feedback, setFeedback] = useStateWithDeps("", [heldState.identity]);

  const [showOverride, setShowOverride] = useState(false);

  // Same flow as the header's manual claim: plain claim first; a 400 means
  // the server still sees an owner (the hold's pseudo-owned state, or another
  // tutor) — offer the override rather than dead-ending
  const { attemptClaim, isPending: isClaimPending } = useClaimSession(
    flowGeneratorSessionId,
    () => setShowOverride(true),
    onClaimed,
  );

  const isEdited = canEdit && text.trim() !== (reply?.message?.trim() ?? "");

  const approve = useMutation({
    // Every approve carries the reviewer's REQUIRED verdict on the flag
    // (markedAsFalsePositive) — the two buttons are the only entry points
    mutationFn: (markedAsFalsePositive?: boolean) =>
      postChatResponseApprove(flowGeneratorSessionId, {
        feedback: feedback.trim() || undefined,
        editedMessage: isEdited ? text : undefined,
        markedAsFalsePositive,
      }),
    onSuccess: (_, markedAsFalsePositive) => {
      // Student-message hold: the AI resumed and re-took the session — it
      // leaves the queue (driven by the session-update refetches below, never
      // assumed here). Reply hold: delivered — the session stays queued and
      // the AI stays paused.
      toast.success(
        student
          ? markedAsFalsePositive
            ? "Flagged as false positive — the AI tutor has resumed and will answer the student's message"
            : "Approved — the AI tutor has resumed and will answer the student's message"
          : markedAsFalsePositive
            ? "Flagged as false positive — reply sent to the student as the AI tutor"
            : "Approved — reply sent to the student as the AI tutor",
      );
      setFeedback("");

      // Dismiss the panel directly — the server's post-approve reads are NOT
      // reliably updated fast enough, so the reviewer's own UI must never wait
      // on the probe reflecting the approve back
      onResolved(heldState.identity);
    },
    onError: (e) => {
      // 404/409/410 are routine races (another tutor acted / claimed) — the
      // shared handler surfaces the server's message and refreshes the row
      toastAndWarn(
        student ? "Could not approve the student's message" : "Could not approve the reply",
        e,
      );
    },
  });

  usePendingToast(approve.isPending, student ? "Approving message..." : "Approving reply...");

  const submitApprove = (markedAsFalsePositive: boolean | undefined, unlock: () => void) => {
    if (approve.isPending || (canEdit && text.trim().length === 0)) {
      unlock();
      return;
    }

    approve
      .mutateAsync(markedAsFalsePositive)
      .catch(() => {
        // Handled by the mutation's onError
      })
      .finally(unlock);
  };

  const metaStudent = holdMeta(student);
  const metaReply = holdMeta(reply);

  return (
    <PanelWrapper>
      <HeaderRow>
        <IconFlag color={t.brandNeutralBlack} />
        <HeaderTitle>
          {student
            ? "AI paused — the student's message needs review before the AI responds"
            : "AI paused — flagged reply needs review before it reaches the student"}
        </HeaderTitle>
      </HeaderRow>
      {student && (
        <>
          <SectionLabel>
            Student message held
            <span>{metaStudent}</span>
          </SectionLabel>
          <MessageBody>{student.message}</MessageBody>
          <AtomsSummary atoms={student.atoms} />
        </>
      )}
      {reply && student && (
        <>
          <SectionLabel>
            AI reply held
            <span>{metaReply}</span>
          </SectionLabel>
          <MessageBody data-right={"true"} data-ai={"true"}>
            {reply.message}
          </MessageBody>
          <AtomsSummary atoms={reply.atoms} />
        </>
      )}
      {reply && !student && (
        <>
          <SectionLabel>Atoms Verdict: {metaReply}</SectionLabel>
          <AtomsSummary atoms={reply.atoms} />
          <textarea
            className={SharedChatStyle}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={approve.isPending}
          />
        </>
      )}
      <FeedbackInput
        placeholder={"Optional reviewer note (audit trail only)..."}
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        disabled={approve.isPending}
      />
      <Row data-width={"min"}>
        {/* STUDENT holds force a verdict on the flag: choosing a button IS the
            classification, so an unclassified approve cannot exist —
            "Preview AI" = the flag stands (markedAsFalsePositive false),
            "Approve" = the judge was wrong / false positive (true). Held AI
            REPLIES don't classify: their lone "Approve & send" sends the draft
            (edited or not) with markedAsFalsePositive left unset. The note
            above applies to whichever is pressed. */}
        <ButtonIcon
          icon={
            student ? (
              <IconFlag width={18} height={18} color={t.brandNeutralWhite} />
            ) : (
              <IconTick width={18} height={18} />
            )
          }
          text={student ? "Preview AI" : "Approve & send"}
          onClick={(unlock) => submitApprove(student ? false : undefined, unlock)}
          backgroundColor={t.brandAccentGreen}
          flare
        />
        {student && (
          <ButtonIcon
            icon={<IconTick width={18} height={18} />}
            text={"Approve"}
            onClick={(unlock) => submitApprove(true, unlock)}
            backgroundColor={t.brandAccentGreen}
            flare
          />
        )}
        <ButtonIcon
          icon={<IconPlus color={t.brandNeutralWhite} width={"14"} height={"14"} />}
          text={isClaimPending ? "Claiming..." : "Claim instead"}
          onClick={(unlock) => {
            // Plain claim, like any other queue row; claiming resolves all
            // holds server-side. A refusal opens the override modal below.
            attemptClaim(false);
            unlock();
          }}
          backgroundColor={t.brandAccentBlue}
          flare
        />
      </Row>
      {showOverride && (
        <ModalSimple
          title={"Override claim this session?"}
          onClick={() => {
            setShowOverride(false);
          }}
        >
          <OverrideText>
            The AI tutor still holds this session while the flagged message is under review.
            <br />
            Claiming will take it over and switch it to human moderation — you will review every
            response before it reaches the student.
          </OverrideText>
          <Row>
            <ButtonIcon
              icon={<IconX color={t.brandNeutralWhite} />}
              text={"Go back"}
              onClick={() => {
                setShowOverride(false);
              }}
              backgroundColor={t.brandAccentRed}
              flare
            />
            <ButtonIcon
              icon={<IconTick color={t.brandNeutralWhite} width={18} height={18} />}
              text={"Claim session"}
              onClick={() => {
                setShowOverride(false);
                attemptClaim(true);
              }}
              backgroundColor={t.brandAccentBlue}
              flare
            />
          </Row>
        </ModalSimple>
      )}
    </PanelWrapper>
  );
};
